import React, { Suspense, lazy, useState, useMemo, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  Divider,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  People,
  Science,
  LocalHospital,
  Receipt,
  Assignment,
  Description,
  Settings,
  ExitToApp,
  FormatColorText,
  Assessment,
  Tune,
  Category,
  Biotech,
  Coronavirus,
  DragIndicator,
  Edit,
  Check,
  BugReport,
  PeopleAlt,
  VerifiedUser,
  Place,
  Inventory2,
  WarningAmber,
  Memory,
  PlaylistAdd,
  VpnKey,
  TableChart,
  CalendarToday,
  CalendarMonth,
  Tv,
  MedicalInformation,
  CloudUpload,
  PointOfSale,
  EventSeat,
  RateReview,
  QueryStats,
  Loyalty,
  RequestQuote,
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useConfig } from '../contexts/ConfigContext';
import { useFeatureFlag } from '../contexts/SystemContext';
import { getGradientCss, useColorMode } from '../themeFromConfig';
import ClinicaModuleGuard from '../components/ClinicaModuleGuard';
import axios from 'axios';
import { API_URL } from '../config';

const VistaPrincipal = lazy(() => import('./VistaPrincipal'));
const Pacientes = lazy(() => import('./Pacientes'));
const Fidelizacion = lazy(() => import('./Fidelizacion'));
const Examenes = lazy(() => import('./Examenes'));
const Medicos = lazy(() => import('./Medicos'));
const Facturacion = lazy(() => import('./Facturacion'));
const Cotizaciones = lazy(() => import('./Cotizaciones'));
const Ordenes = lazy(() => import('./Ordenes'));
const Resultados = lazy(() => import('./Resultados'));
const Bacteriologia = lazy(() => import('./Bacteriologia'));
const Usuarios = lazy(() => import('./Usuarios'));
const Formatos = lazy(() => import('./Formatos'));
const Reportes = lazy(() => import('./Reportes'));
const Configuracion = lazy(() => import('./Configuracion'));
const Perfiles = lazy(() => import('./Perfiles'));
const TiposMuestra = lazy(() => import('./TiposMuestra'));
const ControlCalidad = lazy(() => import('./ControlCalidad'));
const Ubicaciones = lazy(() => import('./Ubicaciones'));
const Inventario = lazy(() => import('./Inventario'));
const Debug = lazy(() => import('./Debug'));
const PanelControlRiesgo = lazy(() => import('./PanelControlRiesgo'));
const ProductividadLISPage = lazy(() => import('./ProductividadLIS'));
const OrdenTecnica = lazy(() => import('./OrdenTecnica'));
const AdminLicencias = lazy(() => import('./AdminLicencias'));
const TablaPrecios = lazy(() => import('./TablaPrecios'));
const ClinicaAgendaPage = lazy(() => import('./clinica/ClinicaAgendaPage'));
const CalendarioCitasPage = lazy(() => import('./clinica/CalendarioCitasPage'));
const ClinicaExpedientesPage = lazy(() => import('./clinica/ClinicaExpedientesPage'));
const ClinicaExpedientePage = lazy(() => import('./clinica/ClinicaExpedientePage'));
const SubirEstudio = lazy(() => import('./clinica/SubirEstudio'));
const ClinicaColaCobroPage = lazy(() => import('./clinica/ClinicaColaCobroPage'));
const ClinicaColaEsperaPage = lazy(() => import('./clinica/ClinicaColaEsperaPage'));
const InterpretarEstudiosPage = lazy(() => import('./clinica/InterpretarEstudiosPage'));
const ClinicaReportesPage = lazy(() => import('./clinica/ClinicaReportesPage'));

function PageFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }} role="status" aria-live="polite">
      <CircularProgress aria-label="Cargando módulo" />
    </Box>
  );
}

const IconoModulo: React.FC<{ bgcolor: string; children: React.ReactNode }> = ({ bgcolor, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 2, bgcolor }}>
    {children}
  </Box>
);

const drawerWidth = 240;
const MENU_ORDER_STORAGE_KEY = 'laboratorio_menu_order';
const CLINICA_MENU_ORDER_STORAGE_KEY = 'laboratorio_menu_order_clinica';

type MenuSection = 'lab' | 'clinica';

function sortMenuByOrder<T extends { modulo: string }>(items: T[], order: string[]): T[] {
  return [...items].sort((a, b) => {
    const ia = order.indexOf(a.modulo);
    const ib = order.indexOf(b.modulo);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

const menuItems = [
  { text: 'Dashboard', icon: <IconoModulo bgcolor="#4CAF50"><DashboardIcon sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/', modulo: 'dashboard' },
  { text: 'Pacientes', icon: <IconoModulo bgcolor="#FF7043"><People sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/pacientes', modulo: 'pacientes' },
  {
    text: 'Fidelización',
    icon: <IconoModulo bgcolor="#E91E63"><Loyalty sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
    path: '/fidelizacion',
    modulo: 'fidelizacion',
  },
  { text: 'Exámenes', icon: <IconoModulo bgcolor="#29B6F6"><Science sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/examenes', modulo: 'examenes' },
  { text: 'Médicos', icon: <IconoModulo bgcolor="#66BB6A"><LocalHospital sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/medicos', modulo: 'medicos' },
  { text: 'Facturación', icon: <IconoModulo bgcolor="#FFA726"><Receipt sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/facturacion', modulo: 'facturacion' },
  { text: 'Cotizaciones', icon: <IconoModulo bgcolor="#FB8C00"><RequestQuote sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/cotizaciones', modulo: 'cotizaciones' },
  {
    text: 'Nueva Orden Técnica',
    icon: <IconoModulo bgcolor="#00897B"><PlaylistAdd sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
    path: '/orden-tecnica',
    modulo: 'orden-tecnica',
  },
  { text: 'Órdenes', icon: <IconoModulo bgcolor="#AB47BC"><Assignment sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/ordenes', modulo: 'ordenes' },
  { text: 'Ingreso de Resultados', icon: <IconoModulo bgcolor="#5C6BC0"><Description sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/resultados', modulo: 'resultados' },
  { text: 'Bacteriología', icon: <IconoModulo bgcolor="#00695C"><Coronavirus sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/bacteriologia', modulo: 'bacteriologia' },
  { text: 'Reportes', icon: <IconoModulo bgcolor="#42A5F5"><Assessment sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/reportes', modulo: 'reportes' },
  {
    text: 'Productividad LIS',
    icon: <IconoModulo bgcolor="#0277BD"><Memory sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
    path: '/reportes/productividad-lis',
    modulo: 'reportes-lis',
  },
  { text: 'Inventario', icon: <IconoModulo bgcolor="#5D4037"><Inventory2 sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/inventario', modulo: 'inventario' },
  { text: 'Control de Calidad', icon: <IconoModulo bgcolor="#00897B"><VerifiedUser sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/control-calidad', modulo: 'control-calidad' },
  { text: 'Panel de Control de Riesgo', icon: <IconoModulo bgcolor="#C62828"><WarningAmber sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/control-riesgo', modulo: 'control-riesgo' },
  { text: 'Perfiles', icon: <IconoModulo bgcolor="#EC407A"><Category sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/perfiles', modulo: 'perfiles' },
  { text: 'Tipos de muestra', icon: <IconoModulo bgcolor="#26A69A"><Biotech sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/tipos-muestra', modulo: 'tipos-muestra' },
  { text: 'Diseño de reportes e impresión', icon: <IconoModulo bgcolor="#78909C"><FormatColorText sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/formatos', modulo: 'formatos' },
  { text: 'Configuración', icon: <IconoModulo bgcolor="#8D6E63"><Tune sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/configuracion', modulo: 'configuracion' },
  { text: 'Usuarios', icon: <IconoModulo bgcolor="#7E57C2"><Settings sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/usuarios', modulo: 'usuarios' },
  { text: 'Ubicaciones', icon: <IconoModulo bgcolor="#5D4037"><Place sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/ubicaciones', modulo: 'ubicaciones' },
];

const clinicaMenuItems = [
  {
    text: 'Agenda médica',
    icon: <IconoModulo bgcolor="#1565C0"><CalendarToday sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
    path: '/clinica/agenda',
    modulo: 'agenda',
  },
  {
    text: 'Calendario de citas',
    icon: <IconoModulo bgcolor="#0277BD"><CalendarMonth sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
    path: '/clinica/calendario',
    modulo: 'calendario-citas',
  },
  {
    text: 'Pantalla de citas',
    icon: <IconoModulo bgcolor="#004D40"><Tv sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
    path: '/tablero-citas',
    modulo: 'tablero-citas',
  },
  {
    text: 'Expedientes clínicos',
    icon: <IconoModulo bgcolor="#2E7D32"><MedicalInformation sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
    path: '/clinica/expedientes',
    modulo: 'hce',
  },
  {
    text: 'Carga de imagenología',
    icon: <IconoModulo bgcolor="#6A1B9A"><CloudUpload sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
    path: '/clinica/subir-estudio',
    modulo: 'estudios-imagen',
  },
  {
    text: 'Cola de cobro',
    icon: <IconoModulo bgcolor="#EF6C00"><PointOfSale sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
    path: '/clinica/cola-cobro',
    modulo: 'cola-cobro',
  },
  {
    text: 'Cola de espera',
    icon: <IconoModulo bgcolor="#00838F"><EventSeat sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
    path: '/clinica/cola-espera',
    modulo: 'cola-espera',
  },
  {
    text: 'Interpretar estudios',
    icon: <IconoModulo bgcolor="#5E35B1"><RateReview sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
    path: '/clinica/interpretar-estudios',
    modulo: 'interpretar-estudios',
  },
  {
    text: 'Reportes clínica',
    icon: <IconoModulo bgcolor="#2E7D32"><QueryStats sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
    path: '/clinica/reportes',
    modulo: 'reportes-clinica',
  },
];

const debugMenuItem = { text: 'Debug', icon: <IconoModulo bgcolor="#D32F2F"><BugReport sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>, path: '/debug', modulo: 'debug' };

const adminLicenciasMenuItem = {
  text: 'Llave maestra comercial',
  icon: <IconoModulo bgcolor="#1565C0"><VpnKey sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
  path: '/admin-licencias',
  modulo: 'admin-licencias',
};

const tablaPreciosMenuItem = {
  text: 'Tabla de precios',
  icon: <IconoModulo bgcolor="#2E7D32"><TableChart sx={{ color: 'white', fontSize: 20 }} /></IconoModulo>,
  path: '/tabla-precios',
  modulo: 'tabla-precios',
};

function pathToModulo(pathname: string): string {
  if (pathname === '/') return 'dashboard';
  const first = pathname.split('/').filter(Boolean)[0];
  if (first === 'reportes') return 'reportes';
  if (first === 'orden-tecnica') return 'orden-tecnica';
  if (first === 'admin-licencias') return 'admin-licencias';
  if (first === 'tabla-precios') return 'tabla-precios';
  if (first === 'clinica') {
    const sub = pathname.split('/').filter(Boolean)[1];
    if (sub === 'expedientes' || sub === 'expediente') return 'hce';
    if (sub === 'subir-estudio') return 'estudios-imagen';
    if (sub === 'cola-cobro') return 'cola-cobro';
    if (sub === 'cola-espera') return 'cola-espera';
    if (sub === 'interpretar-estudios') return 'interpretar-estudios';
    if (sub === 'calendario') return 'calendario-citas';
    if (sub === 'reportes') return 'reportes-clinica';
    if (sub === 'agenda') return 'agenda';
  }
  return first;
}

function isMenuItemActive(
  item: { path: string; modulo: string },
  pathname: string,
  search: string
): boolean {
  if (item.path.includes('?')) return `${pathname}${search}` === item.path;
  if (item.modulo === 'hce') {
    return pathname === '/clinica/expedientes' || pathname.startsWith('/clinica/expediente/');
  }
  if (item.modulo === 'agenda') return pathname === '/clinica/agenda';
  if (item.modulo === 'calendario-citas') return pathname === '/clinica/calendario';
  if (item.modulo === 'estudios-imagen') return pathname === '/clinica/subir-estudio';
  if (item.modulo === 'cola-cobro') return pathname === '/clinica/cola-cobro';
  if (item.modulo === 'cola-espera') return pathname === '/clinica/cola-espera';
  if (item.modulo === 'interpretar-estudios') return pathname === '/clinica/interpretar-estudios';
  if (item.modulo === 'reportes-clinica') return pathname === '/clinica/reportes';
  return pathname === item.path;
}

const defaultMenuOrder = menuItems.map((m) => m.modulo);
const defaultClinicaMenuOrder = clinicaMenuItems.map((m) => m.modulo);

const Dashboard: React.FC = () => {
  const { user, token, logout, permisos, isAdminOrSuper, isSuperUsuario } = useAuth();
  const { config } = useConfig();
  const ordenesSinFacturacion = useFeatureFlag('ordenes_sin_facturacion_habilitado');
  const moduloClinicaHabilitado = useFeatureFlag('modulo_clinica_habilitado');
  const { connected, count, usuariosConectados } = useSocket();
  const isAdmin = isAdminOrSuper;
  const gradientSidebar = getGradientCss(config?.paleta, 'gradientSidebar');
  const gradientPageBg = getGradientCss(config?.paleta, 'gradientPageBg');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode, toggleColorMode } = useColorMode();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editMenuMode, setEditMenuMode] = useState(false);
  const [orderedModulos, setOrderedModulos] = useState<string[]>(() => {
    try {
      const s = localStorage.getItem(MENU_ORDER_STORAGE_KEY);
      if (s) {
        const arr = JSON.parse(s);
        if (Array.isArray(arr) && arr.length) return arr;
      }
    } catch (_) {}
    return defaultMenuOrder;
  });
  const [orderedClinicaModulos, setOrderedClinicaModulos] = useState<string[]>(() => {
    try {
      const s = localStorage.getItem(CLINICA_MENU_ORDER_STORAGE_KEY);
      if (s) {
        const arr = JSON.parse(s);
        if (Array.isArray(arr) && arr.length) return arr;
      }
    } catch (_) {}
    return defaultClinicaMenuOrder;
  });
  const [dragging, setDragging] = useState<{ section: MenuSection; index: number } | null>(null);

  const puedeOrdenTecnica =
    ordenesSinFacturacion && (permisos.includes('ordenes') || permisos.includes('resultados'));
  const tieneFacturacion = permisos.includes('facturacion');

  const menuLabVisibles = useMemo(() => {
    const visible = menuItems.filter((item) => {
      if (isSuperUsuario) {
        if (item.modulo === 'inventario' && !config?.inventario_habilitado) return false;
        if (item.modulo === 'orden-tecnica' && !ordenesSinFacturacion) return false;
        // Módulos desactivados en catálogo no vienen en permisos del superusuario
        if (item.modulo !== 'orden-tecnica' && !permisos.includes(item.modulo)) return false;
        return true;
      }
      if (item.modulo === 'orden-tecnica') return puedeOrdenTecnica;
      if (item.modulo === 'facturacion') return tieneFacturacion;
      if (item.modulo === 'reportes-lis') return permisos.includes('reportes');
      // Fidelización: respetar catálogo aunque el admin vea el resto del menú
      if (item.modulo === 'fidelizacion' && !permisos.includes('fidelizacion')) return false;
      if (!permisos.includes(item.modulo) && !isAdminOrSuper) return false;
      if (item.modulo === 'inventario' && !config?.inventario_habilitado) return false;
      return true;
    });
    return sortMenuByOrder(visible, orderedModulos);
  }, [
    permisos,
    orderedModulos,
    config?.inventario_habilitado,
    isAdminOrSuper,
    isSuperUsuario,
    puedeOrdenTecnica,
    tieneFacturacion,
    ordenesSinFacturacion,
  ]);

  const menuClinicaVisibles = useMemo(() => {
    if (!moduloClinicaHabilitado) return [];
    const accesoClinicaBase =
      isSuperUsuario ||
      isAdminOrSuper ||
      ['agenda', 'hce', 'estudios-imagen'].some((m) => permisos.includes(m));
    const visible = clinicaMenuItems.filter(
      (item) =>
        isSuperUsuario ||
        accesoClinicaBase ||
        permisos.includes(item.modulo) ||
        (item.modulo === 'calendario-citas' && permisos.includes('agenda')) ||
        (item.modulo === 'tablero-citas' && (permisos.includes('agenda') || accesoClinicaBase))
    );
    return sortMenuByOrder(visible, orderedClinicaModulos);
  }, [
    permisos,
    orderedClinicaModulos,
    isAdminOrSuper,
    isSuperUsuario,
    moduloClinicaHabilitado,
  ]);

  const menuExtrasVisibles = useMemo(
    () =>
      user?.username === 'Luisoft'
        ? [tablaPreciosMenuItem, adminLicenciasMenuItem, debugMenuItem]
        : [],
    [user?.username]
  );

  const menuItemsVisibles = useMemo(
    () => [...menuLabVisibles, ...menuClinicaVisibles, ...menuExtrasVisibles],
    [menuLabVisibles, menuClinicaVisibles, menuExtrasVisibles]
  );

  const dragImageRef = React.useRef<HTMLDivElement | null>(null);

  const handleDragStart = (
    e: React.DragEvent,
    section: MenuSection,
    index: number,
    items: { text: string }[]
  ) => {
    setDragging({ section, index });
    e.dataTransfer.setData('text/plain', `${section}:${index}`);
    e.dataTransfer.effectAllowed = 'move';
    const item = items[index];
    if (!item) return;
    const dragImage = document.createElement('div');
    dragImage.setAttribute('role', 'presentation');
    dragImage.style.cssText = [
      'position:absolute; top:-9999px; left:0;',
      'min-width:200px; max-width:220px; padding:8px 12px;',
      'background:#fff; border-radius:8px; box-shadow:0 4px 12px rgba(0,0,0,0.15);',
      'display:flex; align-items:center; gap:8px; font-family:inherit; font-size:14px;',
      'border:1px solid rgba(0,105,92,0.2); white-space:nowrap;',
    ].join(' ');
    dragImage.textContent = item.text;
    document.body.appendChild(dragImage);
    dragImageRef.current = dragImage;
    e.dataTransfer.setDragImage(dragImage, 12, 24);
  };

  const handleDragEnd = () => {
    setDragging(null);
    if (dragImageRef.current && dragImageRef.current.parentNode) {
      dragImageRef.current.parentNode.removeChild(dragImageRef.current);
      dragImageRef.current = null;
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (
    e: React.DragEvent,
    section: MenuSection,
    dropIndex: number,
    items: { modulo: string }[],
    currentOrder: string[],
    setOrder: React.Dispatch<React.SetStateAction<string[]>>,
    storageKey: string
  ) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('text/plain');
    const [dragSection, dragIndexStr] = raw.split(':');
    if (dragSection !== section) return;
    const dragIndex = parseInt(dragIndexStr, 10);
    if (dragIndex === dropIndex || Number.isNaN(dragIndex)) return;
    const reordered = [...items];
    const [removed] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, removed);
    const newOrder = [
      ...reordered.map((i) => i.modulo),
      ...currentOrder.filter((m) => !reordered.some((i) => i.modulo === m)),
    ];
    setOrder(newOrder);
    localStorage.setItem(storageKey, JSON.stringify(newOrder));
  };

  const renderMenuItems = (items: typeof menuItems, section: MenuSection) =>
    items.map((item, index) => (
      <ListItem
        key={item.modulo}
        disablePadding
        draggable={editMenuMode}
        onDragStart={(e) => editMenuMode && handleDragStart(e, section, index, items)}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDrop={(e) =>
          editMenuMode &&
          handleDrop(
            e,
            section,
            index,
            items,
            section === 'lab' ? orderedModulos : orderedClinicaModulos,
            section === 'lab' ? setOrderedModulos : setOrderedClinicaModulos,
            section === 'lab' ? MENU_ORDER_STORAGE_KEY : CLINICA_MENU_ORDER_STORAGE_KEY
          )
        }
        sx={{
          borderRadius: 2,
          cursor: editMenuMode ? 'grab' : 'default',
          '&:active': editMenuMode ? { cursor: 'grabbing' } : {},
          opacity: dragging?.section === section && dragging?.index === index ? 0.4 : 1,
          transition: 'opacity 0.15s',
        }}
      >
        <ListItemButton
          selected={!editMenuMode && isMenuItemActive(item, location.pathname, location.search)}
          onClick={() => {
            if (editMenuMode) return;
            if (item.path === '/tablero-citas') {
              window.open(item.path, '_blank', 'noopener,noreferrer');
            } else {
              navigate(item.path);
            }
            if (isMobile) setMobileOpen(false);
          }}
          sx={{
            borderRadius: 2,
            '& .MuiListItemIcon-root': {
              minWidth: 44,
            },
          }}
        >
          <ListItemIcon>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {editMenuMode && <DragIndicator fontSize="small" color="action" sx={{ cursor: 'grab' }} />}
              {item.icon}
            </Box>
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontWeight:
                !editMenuMode && isMenuItemActive(item, location.pathname, location.search) ? 600 : 500,
            }}
          />
        </ListItemButton>
      </ListItem>
    ));

  useEffect(() => {
    const modulo = pathToModulo(location.pathname);
    if (modulo === 'debug' || modulo === 'admin-licencias' || modulo === 'tabla-precios') {
      if (user?.username !== 'Luisoft') navigate('/', { replace: true });
      return;
    }
    if (modulo === 'orden-tecnica' && !puedeOrdenTecnica && !isSuperUsuario) {
      const primero = menuItemsVisibles[0];
      navigate(primero?.path ?? '/resultados', { replace: true });
      return;
    }
    if (isSuperUsuario) return;
    if (modulo === 'configuracion' && isAdminOrSuper) return;
    if (modulo === 'fidelizacion' && !permisos.includes('fidelizacion')) {
      const primero = menuItemsVisibles[0];
      if (primero) navigate(primero.path, { replace: true });
      else navigate('/ordenes', { replace: true });
      return;
    }
    if (modulo && !permisos.includes(modulo) && modulo !== 'orden-tecnica') {
      if (modulo === 'configuracion' && isAdminOrSuper) return;
      const primero = menuItemsVisibles[0];
      if (primero) navigate(primero.path, { replace: true });
      else navigate('/ordenes', { replace: true });
    }
  }, [
    location.pathname,
    permisos,
    user?.username,
    navigate,
    menuItemsVisibles,
    isAdminOrSuper,
    isSuperUsuario,
    puedeOrdenTecnica,
  ]);

  useEffect(() => {
    if (!isAdminOrSuper || !token) return;
    const modulos = [...menuItems, ...clinicaMenuItems, debugMenuItem]
      .filter((m) => !String(m.modulo).startsWith('__'))
      .map((m) => ({ key: m.modulo, label: m.text }));
    axios.post(
      `${API_URL}/roles/modulos/sync`,
      { modulos },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => undefined);
  }, [isAdminOrSuper, token]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  const drawerPaperSx = {
    width: drawerWidth,
    boxSizing: 'border-box' as const,
    borderRight: '1px solid',
    borderColor: 'divider',
    background: (t: { palette: { mode: string; background: { paper: string } } }) =>
      t.palette.mode === 'dark'
        ? t.palette.background.paper
        : 'linear-gradient(180deg, #FFFFFF 0%, #F8FBFA 100%)',
    boxShadow: '2px 0 12px rgba(0, 77, 64, 0.06)',
  };

  const drawerContent = (
    <>
      <Toolbar />
      <Box sx={{ overflow: 'auto', py: 1 }}>
        <Box sx={{ px: 1, pb: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            size="small"
            onClick={() => setEditMenuMode((prev) => !prev)}
            aria-label={editMenuMode ? 'Terminar reorden de menú' : 'Reordenar menú'}
            sx={{
              minWidth: 40,
              minHeight: 40,
              bgcolor: editMenuMode ? 'primary.main' : 'action.hover',
              color: editMenuMode ? 'primary.contrastText' : 'action.active',
              '&:hover': { bgcolor: editMenuMode ? 'primary.dark' : 'action.selected' },
            }}
          >
            {editMenuMode ? <Check fontSize="small" /> : <Edit fontSize="small" />}
          </IconButton>
        </Box>
        <List dense sx={{ px: 1 }}>
          {renderMenuItems(menuLabVisibles, 'lab')}
          {menuClinicaVisibles.length > 0 && (
            <Divider sx={{ my: 1.25, mx: 1.5, borderColor: 'divider' }} />
          )}
          {renderMenuItems(menuClinicaVisibles, 'clinica')}
          {menuExtrasVisibles.map((item) => (
            <ListItem key={item.modulo} disablePadding sx={{ borderRadius: 2 }}>
              <ListItemButton
                selected={!editMenuMode && isMenuItemActive(item, location.pathname, location.search)}
                onClick={() => {
                  if (editMenuMode) return;
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  '& .MuiListItemIcon-root': { minWidth: 44 },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight:
                      !editMenuMode && isMenuItemActive(item, location.pathname, location.search)
                        ? 600
                        : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          background: gradientSidebar,
          boxShadow: (t) => `0 2px 20px ${t.palette.primary.dark}40`,
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton
            color="inherit"
            edge="start"
            aria-label="Abrir menú de navegación"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' }, minWidth: 44, minHeight: 44 }}
          >
            <MenuIcon />
          </IconButton>
          <Science sx={{ fontSize: 28, color: 'inherit', opacity: 0.95, display: { xs: 'none', sm: 'block' } }} aria-hidden />
          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.25 }}>
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
              {config?.nombre || 'Laboratorio Clínico'}
            </Typography>
            {(config?.direccion || config?.telefono || config?.ciudad || config?.ruc) && (
              <Typography variant="caption" noWrap sx={{ opacity: 0.9, fontSize: '0.7rem', display: { xs: 'none', sm: 'block' } }}>
                {[config?.direccion, config?.telefono && `Tel: ${config.telefono}`, config?.ciudad, config?.ruc && `RUC: ${config.ruc}`].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAdmin && (
              <Tooltip
                title={
                  usuariosConectados.length > 0
                    ? usuariosConectados.map((u) => u.nombre || u.username).join(', ')
                    : 'Ningún usuario conectado'
                }
              >
                <Chip
                  icon={<PeopleAlt sx={{ fontSize: 18 }} />}
                  label={`${count} ${count === 1 ? 'conectado' : 'conectados'}`}
                  size="small"
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    bgcolor: connected ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                    color: 'inherit',
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              </Tooltip>
            )}
            <Typography variant="body2" sx={{ fontWeight: 500, opacity: 0.95, display: { xs: 'none', md: 'block' } }}>
              {user?.nombre}
            </Typography>
            <IconButton
              onClick={handleMenuClick}
              size="small"
              aria-label="Menú de usuario"
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
              sx={{ minWidth: 44, minHeight: 44, bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.4)', color: 'primary.dark' }}>
                {user?.nombre?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{ sx: { borderRadius: 2, mt: 1.5 } }}
          >
            <MenuItem
              onClick={() => {
                toggleColorMode();
                handleMenuClose();
              }}
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon>
                {mode === 'dark' ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
              </ListItemIcon>
              {mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ borderRadius: 1 }}>
              <ListItemIcon>
                <ExitToApp fontSize="small" color="action" />
              </ListItemIcon>
              Cerrar Sesión
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': drawerPaperSx,
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': drawerPaperSx,
        }}
        open
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          background: (t) =>
            t.palette.mode === 'dark' ? t.palette.background.default : gradientPageBg,
          p: { xs: 1.5, sm: 2, md: 3 },
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          minWidth: 0,
        }}
      >
        <Toolbar />
        <Container maxWidth={false} sx={{ maxWidth: '1600px', px: { xs: 0, sm: 1 } }}>
          <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<VistaPrincipal />} />
            <Route path="/pacientes" element={<Pacientes />} />
            <Route path="/fidelizacion" element={<Fidelizacion />} />
            <Route path="/examenes" element={<Examenes />} />
            <Route path="/medicos" element={<Medicos />} />
            <Route path="/facturacion" element={<Facturacion />} />
            <Route path="/cotizaciones" element={<Cotizaciones />} />
            <Route path="/orden-tecnica" element={<OrdenTecnica />} />
            <Route path="/ordenes" element={<Ordenes />} />
            <Route path="/resultados" element={<Resultados />} />
            <Route path="/bacteriologia" element={<Bacteriologia />} />
            <Route path="/bacteriologia/:ordenId/:examenId" element={<Bacteriologia />} />
            <Route path="/reportes" element={<Reportes />} />
            <Route path="/reportes/productividad-lis" element={<ProductividadLISPage />} />
            <Route path="/inventario" element={<Inventario />} />
            <Route path="/control-calidad" element={<ControlCalidad />} />
            <Route path="/control-riesgo" element={<PanelControlRiesgo />} />
            <Route path="/perfiles" element={<Perfiles />} />
            <Route path="/tipos-muestra" element={<TiposMuestra />} />
            <Route path="/formatos" element={<Formatos />} />
            <Route path="/impresion" element={<Navigate to="/formatos?tab=opciones" replace />} />
            <Route path="/configuracion" element={<Configuracion />} />
            <Route path="/usuarios" element={<Usuarios />} />
            <Route path="/ubicaciones" element={<Ubicaciones />} />
            <Route path="/admin-licencias" element={<AdminLicencias />} />
            <Route path="/tabla-precios" element={<TablaPrecios />} />
            <Route path="/debug" element={<Debug />} />
            <Route
              path="/clinica/agenda"
              element={
                <ClinicaModuleGuard>
                  <ClinicaAgendaPage />
                </ClinicaModuleGuard>
              }
            />
            <Route
              path="/clinica/calendario"
              element={
                <ClinicaModuleGuard>
                  <CalendarioCitasPage />
                </ClinicaModuleGuard>
              }
            />
            <Route
              path="/clinica/expedientes"
              element={
                <ClinicaModuleGuard>
                  <ClinicaExpedientesPage />
                </ClinicaModuleGuard>
              }
            />
            <Route
              path="/clinica/expediente/:pacienteId"
              element={
                <ClinicaModuleGuard>
                  <ClinicaExpedientePage />
                </ClinicaModuleGuard>
              }
            />
            <Route
              path="/clinica/atencion/:pacienteId"
              element={
                <ClinicaModuleGuard>
                  <ClinicaExpedientePage />
                </ClinicaModuleGuard>
              }
            />
            <Route
              path="/clinica/subir-estudio"
              element={
                <ClinicaModuleGuard>
                  <SubirEstudio />
                </ClinicaModuleGuard>
              }
            />
            <Route
              path="/clinica/cola-cobro"
              element={
                <ClinicaModuleGuard>
                  <ClinicaColaCobroPage />
                </ClinicaModuleGuard>
              }
            />
            <Route
              path="/clinica/cola-espera"
              element={
                <ClinicaModuleGuard>
                  <ClinicaColaEsperaPage />
                </ClinicaModuleGuard>
              }
            />
            <Route
              path="/clinica/interpretar-estudios"
              element={
                <ClinicaModuleGuard>
                  <InterpretarEstudiosPage />
                </ClinicaModuleGuard>
              }
            />
            <Route
              path="/clinica/reportes"
              element={
                <ClinicaModuleGuard>
                  <ClinicaReportesPage />
                </ClinicaModuleGuard>
              }
            />
          </Routes>
          </Suspense>
        </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;

